import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useMemo } from "react";
import { Box, Typography, Divider, IconButton } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import SaveIcon from "@mui/icons-material/Save";
import { Check } from "lucide-react";
import CommonDrawerLayout from "../../../common_components/CommonDrawerLayout";
import AppButton from "../../../common_components/AppButton";
import { TextInput, TextArea, Dropdown, FormRow, FormField } from "../../../common_components/FormInputs";
import { toast } from "../../../common_components/Toast/ToastUtils";
import { SENDER_OPTIONS, DEFAULT_RECIPIENT_OPTIONS } from "../utils";
const ModuleTree = ({ modules, selectedKey, onSelect }) => {
    const [expandedKeys, setExpandedKeys] = useState(new Set());
    const toggleExpanded = (key) => {
        setExpandedKeys(prev => {
            const next = new Set(prev);
            if (next.has(key))
                next.delete(key);
            else
                next.add(key);
            return next;
        });
    };
    const renderModule = (module, depth) => {
        const hasChildren = !!module.sub_modules?.length;
        const isSelected = selectedKey === module.key;
        const isExpanded = expandedKeys.has(module.key);
        const nameClassNames = [
            "email-configuration-module-tree-name",
            hasChildren ? "email-configuration-module-tree-name-parent" : "",
            depth ? "email-configuration-module-tree-name-indent" : "",
        ].filter(Boolean).join(" ");
        return (_jsxs(Box, { children: [_jsxs(Box, { className: "email-configuration-module-tree-row", children: [hasChildren ? (_jsx(IconButton, { size: "small", onClick: () => toggleExpanded(module.key), sx: { color: "#6B7280" }, children: isExpanded ? (_jsx(KeyboardArrowDownIcon, { sx: { fontSize: 18 } })) : (_jsx(KeyboardArrowRightIcon, { sx: { fontSize: 18 } })) })) : (_jsx(Box, { style: { width: 32 } })), _jsx(Typography, { className: nameClassNames, children: module.module_name }), hasChildren ? (_jsx(Box, {})) : (_jsx(Box, { className: "email-configuration-module-tree-check-cell", children: _jsx("span", { className: `dropdown-checkbox${isSelected ? ' checked' : ''}`, style: { cursor: 'pointer' }, onClick: () => onSelect(module.key), children: isSelected && _jsx(Check, { size: 12 }) }) }))] }), hasChildren && isExpanded && (_jsx(Box, { className: "email-configuration-module-tree-children", children: module.sub_modules?.map((child) => renderModule(child, depth + 1)) }))] }, module.key));
    };
    return (_jsx(Box, { className: "email-configuration-module-tree", children: modules.length > 0 ? (modules.map((module) => (_jsx(Box, { children: renderModule(module, 0) }, module.key)))) : (_jsx(Typography, { className: "email-configuration-module-tree-empty", children: "No modules available" })) }));
};
const AddEditDrawer = ({ open, onClose, configuration, modules, allUsers, onSave, saving = false, mode, }) => {
    const [formData, setFormData] = useState({
        module_key: '',
        notification_key: '',
        sender: '',
        subject: '',
        notes: '',
        default_recipient: [],
        additional_user_ids: [],
        cc: [],
        bcc: [],
    });
    const [errors, setErrors] = useState({});
    const [isChangingModule, setIsChangingModule] = useState(false);
    useEffect(() => {
        if (open) {
            setIsChangingModule(false);
            if (mode === 'edit' && configuration) {
                setFormData({
                    _id: configuration._id,
                    module_key: configuration.module_key || '',
                    notification_key: configuration.notification_key,
                    sender: configuration.sender,
                    subject: configuration.subject || '',
                    notes: configuration.notes || '',
                    default_recipient: configuration.default_recipient || [],
                    additional_user_ids: configuration.additional_user_ids?.map(u => u._id) || [],
                    cc: configuration.cc?.map(u => u._id) || [],
                    bcc: configuration.bcc?.map(u => u._id) || [],
                });
            }
            else {
                setFormData({
                    module_key: '',
                    notification_key: '',
                    sender: '',
                    subject: '',
                    notes: '',
                    default_recipient: [],
                    additional_user_ids: [],
                    cc: [],
                    bcc: [],
                });
            }
            setErrors({});
        }
    }, [open, configuration, mode]);
    const sortedModules = useMemo(() => {
        const sortModules = (moduleList) => {
            return [...moduleList]
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                .map((module) => ({
                ...module,
                sub_modules: module.sub_modules ? sortModules(module.sub_modules) : [],
            }));
        };
        return sortModules(modules);
    }, [modules]);
    const selectedModuleName = useMemo(() => {
        if (!formData.module_key)
            return '';
        const findName = (list) => {
            for (const m of list) {
                if (m.key === formData.module_key)
                    return m.module_name;
                if (m.sub_modules?.length) {
                    const found = findName(m.sub_modules);
                    if (found)
                        return found;
                }
            }
            return '';
        };
        return findName(sortedModules);
    }, [formData.module_key, sortedModules]);
    const userOptions = useMemo(() => {
        return allUsers.map(user => ({
            value: user._id,
            label: `${user.first_name} ${user.last_name}`,
        }));
    }, [allUsers]);
    const userMap = useMemo(() => {
        return new Map(allUsers.map(u => [u._id, u]));
    }, [allUsers]);
    const validateForm = () => {
        const newErrors = {};
        if (!formData.notification_key.trim()) {
            newErrors.notification_key = 'Notification key is required';
        }
        if (!formData.sender) {
            newErrors.sender = 'Sender is required';
        }
        const duplicateUserIds = new Set();
        const recipientFields = [
            { field: 'additional_user_ids', values: formData.additional_user_ids },
            { field: 'cc', values: formData.cc },
            { field: 'bcc', values: formData.bcc },
        ];
        const seen = new Map();
        recipientFields.forEach(({ field, values }) => {
            values.forEach((id) => {
                if (seen.has(id) && seen.get(id) !== field) {
                    duplicateUserIds.add(id);
                }
                seen.set(id, field);
            });
        });
        if (duplicateUserIds.size > 0) {
            newErrors.additional_user_ids = 'Duplicate recipients are not allowed across Additional Recipients, CC, and BCC';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const selectModuleKey = (key) => {
        setFormData(prev => ({
            ...prev,
            module_key: prev.module_key === key ? '' : key,
        }));
        setIsChangingModule(false);
    };
    const clearFieldError = (field) => {
        setErrors(prev => {
            if (!prev[field])
                return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };
    const handleInputChange = (field) => (e) => {
        setFormData(prev => ({ ...prev, [field]: e.target.value }));
        clearFieldError(field);
    };
    const handleSenderChange = (value) => {
        setFormData(prev => ({ ...prev, sender: value }));
        clearFieldError('sender');
    };
    const handleUserFieldChange = (field) => (value) => {
        const newIds = value;
        const fieldLabels = {
            additional_user_ids: 'Additional Recipients',
            cc: 'CC',
            bcc: 'BCC',
        };
        const otherFields = ['additional_user_ids', 'cc', 'bcc'].filter(f => f !== field);
        const currentIds = formData[field];
        const addedIds = newIds.filter(id => !currentIds.includes(id));
        for (const addedId of addedIds) {
            for (const other of otherFields) {
                if (formData[other].includes(addedId)) {
                    const user = userMap.get(addedId);
                    const userName = user ? `${user.first_name} ${user.last_name}` : 'This user';
                    toast.error(`${userName} is already in ${fieldLabels[other]}`);
                    return;
                }
            }
        }
        setFormData(prev => ({ ...prev, [field]: newIds }));
    };
    const renderUserOption = (option, isSelected) => {
        const user = userMap.get(option.value);
        return (_jsxs(Box, { className: "email-configuration-user-option", children: [_jsx(Box, { className: `email-configuration-user-option-checkbox${isSelected ? " is-checked" : ""}`, children: isSelected && _jsx(Check, { size: 10, color: "white" }) }), _jsxs(Box, { children: [_jsx(Typography, { className: "email-configuration-user-option-name", children: option.label }), user?.email && (_jsx(Typography, { className: "email-configuration-user-option-email", children: user.email }))] })] }));
    };
    const handleSave = () => {
        if (validateForm())
            onSave(formData);
    };
    const headerActions = (_jsx(AppButton, { variant: "contained", color: "primary", size: "small", startIcon: _jsx(SaveIcon, { sx: { fontSize: '16px !important' } }), onClick: handleSave, disabled: saving, className: "email-configuration-save-button", children: saving ? 'Saving...' : 'Save' }));
    return (_jsx(CommonDrawerLayout, { open: open, onClose: onClose, title: _jsx(Typography, { className: "email-configuration-drawer-title", children: mode === 'add' ? 'Add Email Configuration' : 'Edit Email Configuration' }), headerActions: headerActions, width: "md", children: _jsx(Box, { className: "email-configuration-drawer-wrapper", children: _jsxs(Box, { className: "email-configuration-drawer-card", children: [_jsx(FormRow, { columns: 1, gap: 16, children: _jsxs(FormField, { children: [_jsx(Typography, { className: "form-input-label", children: "Module" }), formData.module_key && !isChangingModule ? (_jsxs(Box, { className: "email-configuration-selected-module-row", children: [_jsxs(Box, { className: "email-configuration-selected-module-left", children: [_jsx("span", { className: "dropdown-checkbox checked", children: _jsx(Check, { size: 12 }) }), _jsx(Typography, { className: "email-configuration-selected-module-name", children: selectedModuleName || formData.module_key })] }), _jsx(AppButton, { variant: "text", size: "small", onClick: () => setIsChangingModule(true), className: "email-configuration-selected-module-change", children: "Change" })] })) : (_jsxs(Box, { children: [_jsx(ModuleTree, { modules: sortedModules, selectedKey: formData.module_key, onSelect: selectModuleKey }), formData.module_key && isChangingModule && (_jsx(Box, { sx: { display: 'flex', justifyContent: 'flex-end', mt: 1 }, children: _jsx(AppButton, { variant: "text", size: "small", onClick: () => setIsChangingModule(false), children: "Cancel" }) }))] }))] }) }), _jsxs(FormRow, { columns: 2, gap: 16, children: [_jsx(FormField, { children: _jsx(TextInput, { label: "Notification Key", placeholder: "e.g., invoice_created", value: formData.notification_key, onChange: handleInputChange('notification_key'), required: true, error: !!errors.notification_key, errorMessage: errors.notification_key }) }), _jsx(FormField, { children: _jsx(Dropdown, { label: "Sender", value: formData.sender, onChange: handleSenderChange, options: SENDER_OPTIONS, placeholder: "Select sender...", required: true, error: !!errors.sender, errorMessage: errors.sender }) })] }), _jsx(FormRow, { columns: 1, gap: 16, children: _jsx(FormField, { children: _jsx(TextInput, { label: "Subject", placeholder: "e.g., Draft Email for Approval", value: formData.subject, onChange: handleInputChange('subject') }) }) }), _jsx(Divider, { sx: { my: 2 } }), _jsxs(Box, { className: "email-configuration-recipients-grid", children: [_jsx(Box, { className: "email-configuration-recipients-cell", children: _jsx(Dropdown, { label: "Default Recipients", value: formData.default_recipient, onChange: (value) => setFormData(prev => ({ ...prev, default_recipient: value })), options: DEFAULT_RECIPIENT_OPTIONS, placeholder: "Select recipients...", multiple: true, searchable: true }) }), _jsx(Box, { className: "email-configuration-recipients-cell", children: _jsx(Dropdown, { label: "Additional Recipients", value: formData.additional_user_ids, onChange: handleUserFieldChange('additional_user_ids'), options: userOptions, placeholder: "Select users...", multiple: true, searchable: true, renderOption: renderUserOption }) }), _jsx(Box, { className: "email-configuration-recipients-cell", children: _jsx(Dropdown, { label: "CC", value: formData.cc, onChange: handleUserFieldChange('cc'), options: userOptions, placeholder: "Select CC users...", multiple: true, searchable: true, renderOption: renderUserOption }) }), _jsx(Box, { className: "email-configuration-recipients-cell", children: _jsx(Dropdown, { label: "BCC", value: formData.bcc, onChange: handleUserFieldChange('bcc'), options: userOptions, placeholder: "Select BCC users...", multiple: true, searchable: true, renderOption: renderUserOption }) })] }), _jsx(Divider, { sx: { my: 2 } }), _jsx(FormRow, { columns: 1, gap: 16, className: "email-configuration-notes-row", children: _jsx(FormField, { children: _jsx(TextArea, { label: "Notes", value: formData.notes, onChange: (e) => {
                                    setFormData(prev => ({ ...prev, notes: e.target.value }));
                                }, placeholder: "Enter Notes here", minRows: 4 }) }) })] }) }) }));
};
export default AddEditDrawer;
